import * as ToggleGroup from '@radix-ui/react-toggle-group'
import * as React from 'react'

import { styled } from '~/styled'

import { Icon } from '../icon/Icon'
import { modernVariant, standardVariant } from './ToggleGroupItem'

export const StyledButton = styled(
  ToggleGroup.Item,
  {
    base: [
      'cursor-pointer',
      'disabled:cursor-not-allowed',
      'disabled:opacity-30',
      'grow',
      'flex',
      'items-center',
      'justify-center',
      'font-semibold',
      'leading-none',
      'py-1'
    ],
    variants: {
      size: {
        sm: ['*:not-last:mr-2', 'min-h-8', 'px-4', 'text-sm'],
        md: ['*:not-last:mr-3', 'min-h-10', 'px-8', 'text-md'],
        lg: ['*:not-last:mr-3', 'min-h-12', 'px-8', 'text-lg']
      },
      isIconOnly: {
        true: []
      },
      theme: {
        standard: standardVariant,
        modern: modernVariant
      }
    },
    compoundVariants: [
      {
        isIconOnly: true,
        size: 'sm',
        class: ['min-w-8', 'p-0']
      },
      {
        isIconOnly: true,
        size: 'md',
        class: ['min-w-10', 'p-0']
      },
      {
        isIconOnly: true,
        size: 'lg',
        class: ['min-w-12', 'p-0']
      },
      {
        isIconOnly: true,
        size: 'sm',
        theme: 'modern',
        class: ['min-w-10', 'p-0']
      },
      {
        isIconOnly: true,
        size: 'md',
        theme: 'modern',
        class: ['min-w-12', 'p-0']
      },
      {
        isIconOnly: true,
        size: 'lg',
        theme: 'modern',
        class: ['min-w-15', 'p-0']
      }
    ]
  },
  { enabledResponsiveVariants: true }
)

export const ToggleGroupButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof StyledButton>
>(({ size = 'md', theme = 'standard', children, ...rest }, ref) => {
  const childrenArray = React.Children.toArray(children)
  const isSingleChild = childrenArray.length <= 1
  const isIconOnly =
    isSingleChild &&
    React.isValidElement(childrenArray[0]) &&
    childrenArray[0]?.type === Icon

  return (
    <StyledButton
      ref={ref}
      size={size}
      isIconOnly={isIconOnly}
      theme={theme}
      {...rest}
    >
      {
        childrenArray.map((child) => {
          if (
            !isSingleChild &&
            (typeof child === 'string' || typeof child === 'number')
          )
            return <span key={child}>{child}</span>
          if (
            React.isValidElement<React.ComponentPropsWithoutRef<typeof Icon>>(
              child
            )
          ) {
            if (child?.type === Icon)
              return React.cloneElement(child, { ...child.props, size })
          }
          return child
        }) as React.ReactElement<unknown>[]
      }
    </StyledButton>
  )
})
